/**
 * WidgetEmptyState — the ONE "nothing here" rendering for dashboard widgets.
 *
 * Reported 2026-10-21: "Nothing pending your approval." sat as one grey line pinned to the
 * top of an otherwise empty widget. A census found thirteen empty states in four styles —
 * nine top-pinned `py: 3` lines, three centred blocks with a 40–48px grey glyph, one centred
 * caption. The owner named the licence-required state as the reference, so this has the
 * same anatomy as WidgetErrorState: fills the frame, centred on both axes, a 36px tinted
 * circle, a 600-weight line, an optional caption.
 *
 * The tint is NEUTRAL on purpose. Licence uses primary (an action is available), error uses
 * error.main; an empty widget is neither, and colouring it would claim something it isn't.
 *
 * Also used inside page cards that have their own border (System → Billing activity), where
 * a second bordered empty box would nest a card in a card (svdy-ui §15).
 *
 * `tone="error"` is for a section that FAILED to load and has no WidgetErrorState of its
 * own: same anatomy, error tint — never the neutral one, which would read as "empty".
 *
 * No horizontal padding: widgets inherit one gutter from Dashboard.tsx
 * (widgets/widgetContentAxis.test.ts); `maxWidth` bounds the caption instead.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import type { SvgIconComponent } from '@mui/icons-material';

interface Props {
  icon: SvgIconComponent;
  title: string;
  /** Optional second line — what will appear here, or where to add it. */
  hint?: string;
  tone?: 'neutral' | 'error';
}

const WidgetEmptyState: React.FC<Props> = ({ icon: Icon, title, hint, tone = 'neutral' }) => (
  <Box
    data-widget-empty
    sx={{
      height: '100%',
      minHeight: 120,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      py: 2,
      gap: 1,
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        bgcolor: tone === 'error' ? 'rgba(244,67,54,0.08)' : 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon sx={{ fontSize: 18, color: tone === 'error' ? 'error.main' : 'text.secondary' }} />
    </Box>
    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', maxWidth: 280 }}>
      {title}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 260, lineHeight: 1.45 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

export default WidgetEmptyState;
